// PricingPlan.js
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import './PricingPlan.css';

const ACCENT = '#ef6a1d';
const ACCENT_HOVER = '#d85a10';
const NAVY = '#0b2447';

const WHATSAPP_URL = 'https://wa.me/919510980382';

/* Normalise price strings like "Rs 7500" → "₹7,500" */
const formatPrice = (raw = '') => {
  const num = parseFloat(String(raw).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(num)) return raw;
  return `₹${num.toLocaleString('en-IN')}`;
};

const PricingPlan = ({
  title,
  price,
  features,
  period,
  featured = false,
  badge,
  onBuy,
  index = 0,
}) => {
  const titleLower = (title || '').toLowerCase();
  const detectedPeriod =
    period ||
    (titleLower.includes('quarter')
      ? 'per quarter'
      : titleLower.includes('month')
      ? 'per month'
      : titleLower.includes('year') || titleLower.includes('annual')
      ? 'per year'
      : 'one-time');

  const handleBuy = () => {
    if (onBuy) return onBuy();
    const msg = encodeURIComponent(
      `Hi, I'm interested in the ${title} plan (${price}). Please share more details.`
    );
    window.open(`${WHATSAPP_URL}?text=${msg}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      style={{ display: 'flex', width: '100%', height: '100%' }}
    >
      <Box
        className={`pricing-plan ${featured ? 'is-featured' : ''}`}
        sx={{
          position: 'relative',
          width: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          p: { xs: 3.5, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
          ...(featured
            ? {
                color: '#fff',
                background: `linear-gradient(160deg, #0b2447 0%, #061a36 50%, #123a73 100%)`,
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow:
                  '0 30px 60px -15px rgba(11,36,71,0.4), 0 0 0 1px rgba(239,106,29,0.4)',
                /* Subtle orange glow inside the card */
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: -80,
                  right: -80,
                  width: 240,
                  height: 240,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(239,106,29,0.32) 0%, rgba(239,106,29,0) 70%)',
                  pointerEvents: 'none',
                },
                /* Grid pattern */
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                  backgroundSize: '48px 48px',
                  maskImage:
                    'radial-gradient(ellipse at top right, #000 30%, transparent 80%)',
                  WebkitMaskImage:
                    'radial-gradient(ellipse at top right, #000 30%, transparent 80%)',
                  pointerEvents: 'none',
                },
              }
            : {
                color: NAVY,
                background: '#fff',
                border: '1px solid rgba(11,36,71,0.08)',
                boxShadow: '0 20px 50px -20px rgba(11,36,71,0.18)',
                /* Top color edge */
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: `linear-gradient(90deg, ${ACCENT} 0%, #f59e0b 100%)`,
                },
              }),
          '&:hover': {
            transform: 'translateY(-8px)',
            ...(featured
              ? {
                  boxShadow:
                    '0 40px 70px -20px rgba(239,106,29,0.35), 0 30px 60px -15px rgba(11,36,71,0.4), 0 0 0 1px rgba(239,106,29,0.55)',
                }
              : {
                  borderColor: 'rgba(239,106,29,0.35)',
                  boxShadow:
                    '0 30px 60px -20px rgba(239,106,29,0.25), 0 20px 40px -20px rgba(11,36,71,0.18)',
                }),
          },
        }}
      >
        {/* Featured / custom badge */}
        {(featured || badge) && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.4,
              py: 0.45,
              borderRadius: 5,
              background: featured ? ACCENT : 'rgba(239,106,29,0.12)',
              border: featured
                ? '1px solid rgba(255,255,255,0.2)'
                : '1px solid rgba(239,106,29,0.3)',
              color: featured ? '#fff' : ACCENT,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: 0.7,
              zIndex: 2,
              boxShadow: featured
                ? '0 8px 20px rgba(239,106,29,0.4)'
                : 'none',
            }}
          >
            <StarIcon sx={{ fontSize: '0.85rem' }} />
            {badge || 'MOST POPULAR'}
          </Box>
        )}

        {/* Header */}
        <Box sx={{ position: 'relative', zIndex: 1, mb: 3 }}>
          <Box
            sx={{
              fontSize: '0.78rem',
              fontWeight: 700,
              color: featured ? ACCENT : '#64748b',
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              mb: 0.5,
            }}
          >
            {detectedPeriod}
          </Box>
          <Box
            component="h3"
            sx={{
              m: 0,
              fontSize: { xs: '1.4rem', md: '1.6rem' },
              fontWeight: 800,
              letterSpacing: -0.3,
              color: featured ? '#fff' : NAVY,
            }}
          >
            {title}
          </Box>
        </Box>

        {/* Price */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.8,
            mb: 3.5,
            pb: 3,
            borderBottom: featured
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(11,36,71,0.08)',
          }}
        >
          <Box
            sx={{
              fontSize: { xs: '2.4rem', md: '3rem' },
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: -1.5,
              color: featured ? '#fff' : NAVY,
              fontVariantNumeric: 'tabular-nums',
              background: featured
                ? `linear-gradient(90deg, #fff 0%, #f59e0b 100%)`
                : 'none',
              WebkitBackgroundClip: featured ? 'text' : 'unset',
              WebkitTextFillColor: featured ? 'transparent' : 'unset',
            }}
          >
            {formatPrice(price)}
          </Box>
          <Box
            sx={{
              fontSize: '0.9rem',
              fontWeight: 500,
              color: featured ? '#cbd5e1' : '#64748b',
            }}
          >
            {detectedPeriod}
          </Box>
        </Box>

        {/* Features */}
        <Stack
          spacing={1.2}
          sx={{ position: 'relative', zIndex: 1, mb: 4, flex: 1 }}
        >
          {features.map((feature, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.2,
                fontSize: '0.92rem',
                color: featured ? '#e2e8f0' : '#334155',
                lineHeight: 1.55,
              }}
            >
              <CheckCircleIcon
                sx={{
                  fontSize: '1.05rem',
                  color: featured ? ACCENT : '#22c55e',
                  mt: 0.2,
                  flexShrink: 0,
                }}
              />
              {feature}
            </Box>
          ))}
        </Stack>

        {/* CTA */}
        <Button
          fullWidth
          size="large"
          onClick={handleBuy}
          startIcon={<WhatsAppIcon />}
          endIcon={<ArrowForwardIcon />}
          sx={{
            position: 'relative',
            zIndex: 1,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.98rem',
            py: 1.3,
            borderRadius: 2,
            transition: 'all 0.25s ease',
            ...(featured
              ? {
                  background: ACCENT,
                  color: '#fff',
                  boxShadow: '0 12px 28px rgba(239,106,29,0.45)',
                  '&:hover': {
                    background: ACCENT_HOVER,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 16px 32px rgba(239,106,29,0.55)',
                  },
                }
              : {
                  background: NAVY,
                  color: '#fff',
                  boxShadow: '0 10px 24px rgba(11,36,71,0.25)',
                  '&:hover': {
                    background: '#061a36',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 14px 28px rgba(11,36,71,0.35)',
                  },
                }),
          }}
        >
          Buy This Plan
        </Button>
      </Box>
    </motion.div>
  );
};

export default PricingPlan;
